import React, { useState, useEffect } from 'react';
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Chip from "@material-ui/core/Chip";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import api from "../../../services/api";

const useStyles = makeStyles((theme) => ({
    panel: {
        borderLeft: "1px solid #eee",
        padding: "15px",
        background: "#fcfcfc",
        width: "300px",
        display: "flex",
        flexDirection: "column",
        gap: "10px"
    },
    title: {
        fontWeight: "bold",
        marginBottom: "10px"
    }
}));

export default ({ selectedNode, setNodes, allNodes }) => {
    const classes = useStyles();
    const [label, setLabel] = useState("");
    const [url, setUrl] = useState("");
    const [buttons, setButtons] = useState([]);
    const [listItems, setListItems] = useState([]);

    // PIX Payment fields
    const [pixTitle, setPixTitle] = useState("");
    const [pixAmount, setPixAmount] = useState(0);
    const [pixKey, setPixKey] = useState("");
    const [pixType, setPixType] = useState("EVP");
    const [pixName, setPixName] = useState("");
    const [pixInvoiceNumber, setPixInvoiceNumber] = useState("");
    const [pixText, setPixText] = useState("");
    const [pixFooter, setPixFooter] = useState("");
    const [pixItemName, setPixItemName] = useState("");

    // Trigger Condition fields
    const [triggers, setTriggers] = useState([]);

    // Tag Block fields
    const [tagOptions, setTagOptions] = useState([]);
    const [tagsLoading, setTagsLoading] = useState(false);
    const [selectedTags, setSelectedTags] = useState([]);

    // ChatGPT fields
    const [chatgptApiKey, setChatgptApiKey] = useState("");
    const [chatgptModel, setChatgptModel] = useState("gpt-3.5-turbo");
    const [chatgptSystemPrompt, setChatgptSystemPrompt] = useState("");
    const [chatgptTemperature, setChatgptTemperature] = useState(0.7);
    const [chatgptMaxTokens, setChatgptMaxTokens] = useState(500);
    const [chatgptHistoryLimit, setChatgptHistoryLimit] = useState(5);

    // Return to ChatGPT fields
    const [returnMessage, setReturnMessage] = useState("");

    // Intent Detection local state (for UI control)
    const [intentDetectionEnabled, setIntentDetectionEnabled] = useState(false);
    const [intentsList, setIntentsList] = useState([]);

    // Perplexity fields
    const [perplexityApiKey, setPerplexityApiKey] = useState("");
    const [perplexityModel, setPerplexityModel] = useState("sonar");
    const [perplexitySystemPrompt, setPerplexitySystemPrompt] = useState("");
    const [perplexityTemperature, setPerplexityTemperature] = useState(0.7);
    const [perplexityMaxTokens, setPerplexityMaxTokens] = useState(500);
    const [perplexityHistoryLimit, setPerplexityHistoryLimit] = useState(5);
    const [perplexityReturnQuestions, setPerplexityReturnQuestions] = useState(false);

    // Perplexity Intent Detection local state
    const [perplexityIntentEnabled, setPerplexityIntentEnabled] = useState(false);
    const [perplexityIntentsList, setPerplexityIntentsList] = useState([]);

    // Return to Perplexity fields
    const [returnPerplexityMessage, setReturnPerplexityMessage] = useState("");

    // New node fields
    const [priceValue, setPriceValue] = useState(0);
    const [targetNodeId, setTargetNodeId] = useState("");
    const [targetNodeName, setTargetNodeName] = useState("");

    // Products fields
    const [products, setProducts] = useState([]);
    const [productsLoading, setProductsLoading] = useState(false);
    const [selectedProducts, setSelectedProducts] = useState([]);

    useEffect(() => {
        if (selectedNode) {
            setLabel(selectedNode.data.label || "");
            setUrl(selectedNode.data.url || "");
            setButtons(selectedNode.data.buttons || []);
            setListItems(selectedNode.data.listItems || []);

            // PIX Payment fields
            if (selectedNode.type === 'pix_payment') {
                setPixTitle(selectedNode.data.title || "");
                setPixAmount(selectedNode.data.amount || 0);
                setPixKey(selectedNode.data.pixKey || "");
                setPixType(selectedNode.data.pixType || "EVP");
                setPixName(selectedNode.data.pixName || "");
                setPixInvoiceNumber(selectedNode.data.invoiceNumber || "");
                setPixText(selectedNode.data.text || "");
                setPixFooter(selectedNode.data.footer || "");
                setPixItemName(selectedNode.data.itemName || "");
            }

            // Trigger Condition fields
            if (selectedNode.type === 'trigger_condition') {
                setTriggers(selectedNode.data.triggers || []);
            }

            // Tag Block fields
            if (selectedNode.type === 'tag_block') {
                setSelectedTags(selectedNode.data.tags || []);
            }

            // ChatGPT fields
            if (selectedNode.type === 'chatgpt') {
                setChatgptApiKey(selectedNode.data.apiKey || "");
                setChatgptModel(selectedNode.data.model || "gpt-3.5-turbo");
                setChatgptSystemPrompt(selectedNode.data.systemPrompt || "");
                setChatgptTemperature(selectedNode.data.temperature || 0.7);
                setChatgptMaxTokens(selectedNode.data.maxTokens || 500);
                setChatgptHistoryLimit(selectedNode.data.conversationHistory || 5);

                // Initialize intent detection state
                setIntentDetectionEnabled(!!(selectedNode.data.intentDetection?.enabled));
                setIntentsList(selectedNode.data.intentDetection?.intents || []);
            }

            // Perplexity fields
            if (selectedNode.type === 'perplexity') {
                setPerplexityApiKey(selectedNode.data.apiKey || "");
                setPerplexityModel(selectedNode.data.model || "llama-3.1-sonar-small-128k-online");
                setPerplexitySystemPrompt(selectedNode.data.systemPrompt || "");
                setPerplexityTemperature(selectedNode.data.temperature || 0.7);
                setPerplexityMaxTokens(selectedNode.data.maxTokens || 500);
                setPerplexityHistoryLimit(selectedNode.data.conversationHistory || 5);
                setPerplexityReturnQuestions(selectedNode.data.returnRelatedQuestions || false);

                // Initialize intent detection state
                setPerplexityIntentEnabled(!!(selectedNode.data.intentDetection?.enabled));
                setPerplexityIntentsList(selectedNode.data.intentDetection?.intents || []);
            }

            // Return to ChatGPT fields
            if (selectedNode.type === 'return_to_chatgpt') {
                setReturnMessage(selectedNode.data.message || "Pronto! Posso ajudar com mais alguma coisa?");
            }

            // Return to Perplexity fields
            if (selectedNode.type === 'return_to_perplexity') {
                setReturnPerplexityMessage(selectedNode.data.message || "Pronto! Posso ajudar com mais alguma coisa?");
            }

            // Price fields
            if (selectedNode.type === 'price') {
                setPriceValue(selectedNode.data.value || 0);
            }

            // Jump fields
            if (selectedNode.type === 'jump') {
                setTargetNodeId(selectedNode.data.targetNodeId || "");
                setTargetNodeName(selectedNode.data.targetNodeName || "");
            }

            // Products fields
            if (selectedNode.type === 'products') {
                setSelectedProducts(selectedNode.data.selectedProducts || []);
            }
        }
    }, [selectedNode]);

    // Load products when products node is selected
    useEffect(() => {
        const fetchProducts = async () => {
            if (selectedNode && selectedNode.type === 'products') {
                setProductsLoading(true);
                try {
                    const { data } = await api.get('/products');
                    setProducts(data);
                } catch (error) {
                    console.error('[PropertiesPanel] Error loading products:', error);
                } finally {
                    setProductsLoading(false);
                }
            }
        };
        fetchProducts();
    }, [selectedNode?.type]);

    const handleChange = (e) => {
        setLabel(e.target.value);
        updateNodeData({ label: e.target.value });
    };

    const handleUrlChange = (e) => {
        setUrl(e.target.value);
        updateNodeData({ url: e.target.value });
    };

    const handleButtonChange = (index, value) => {
        const newButtons = [...buttons];
        newButtons[index].label = value;
        setButtons(newButtons);
        updateNodeData({ buttons: newButtons });
    };

    const handleAddButton = () => {
        const newButtons = [...buttons, { id: `btn-${new Date().getTime()}`, label: "New Option" }];
        setButtons(newButtons);
        updateNodeData({ buttons: newButtons });
    };

    const handleRemoveButton = (index) => {
        const newButtons = [...buttons];
        newButtons.splice(index, 1);
        setButtons(newButtons);
        updateNodeData({ buttons: newButtons });
    };

    const handleListItemChange = (index, field, value) => {
        const newItems = [...listItems];
        newItems[index][field] = value;
        setListItems(newItems);
        updateNodeData({ listItems: newItems });
    };

    const handleAddListItem = () => {
        const newItems = [...listItems, { id: `item-${new Date().getTime()}`, title: "Novo Item", description: "" }];
        setListItems(newItems);
        updateNodeData({ listItems: newItems });
    };

    const handleRemoveListItem = (index) => {
        const newItems = [...listItems];
        newItems.splice(index, 1);
        setListItems(newItems);
        updateNodeData({ listItems: newItems });
    };

    const updateNodeData = (newData) => {
        console.log("[PropertiesPanel] updateNodeData called with:", newData);
        setNodes((nds) =>
            nds.map((node) => {
                if (node.id === selectedNode.id) {
                    console.log("[PropertiesPanel] Updating node:", node.id);
                    // Create a new node object to trigger React re-render
                    return {
                        ...node,
                        data: {
                            ...node.data,
                            ...newData,
                        }
                    };
                }
                return node;
            })
        );
    };

    if (!selectedNode) {
        return (
            <aside className={classes.panel}>
                <div className={classes.title}>Properties</div>
                <div>Select a node to edit its properties.</div>
            </aside>
        );
    }

    return (
        <aside className={classes.panel}>
            <div className={classes.title}>Properties</div>
            <div style={{ fontSize: "12px", color: "#666", marginBottom: "10px" }}>
                Type: {selectedNode.type}
            </div>

            {(selectedNode.type === 'text' || selectedNode.type === 'input' || selectedNode.type === 'specialties' || selectedNode.type === 'services' || selectedNode.type === 'professionals' || selectedNode.type === 'date_selection' || selectedNode.type === 'hours_selection' || selectedNode.type === 'confirmation' || selectedNode.type === 'start_order' || selectedNode.type === 'order_address' || selectedNode.type === 'order_summary' || selectedNode.type === 'queue_selection') && (
                <TextField
                    label={selectedNode.type === 'start_order' ? "Descrição (Opcional)" : (selectedNode.type === 'order_address' ? "Instruções de Endereço" : "Message / Question")}
                    multiline
                    rows={4}
                    variant="outlined"
                    value={label}
                    onChange={handleChange}
                    fullWidth
                    placeholder={selectedNode.type === 'order_address' ? "Ex: Por favor, me informe o seu endereço para entrega:" : ""}
                />
            )}

            {selectedNode.type === 'start_order' && (
                <Box mt={1} p={1} style={{ backgroundColor: "#e8f5e9", borderRadius: 4, border: "1px solid #4caf50" }}>
                    <Typography variant="caption" style={{ color: "#2e7d32", fontWeight: "bold" }}>
                        💡 Blocos após este serão capturados como parte do pedido até o fluxo ser finalizado.
                    </Typography>
                </Box>
            )}

            {selectedNode.type === 'price' && (
                <TextField
                    label="Valor do Produto (R$)"
                    type="number"
                    variant="outlined"
                    value={priceValue}
                    onChange={(e) => {
                        const val = parseFloat(e.target.value) || 0;
                        setPriceValue(val);
                        updateNodeData({ value: val });
                    }}
                    fullWidth
                    inputProps={{ step: "0.01", min: "0" }}
                    helperText="Este valor será somado ao pedido e não será mostrado na conversa."
                />
            )}

            {selectedNode.type === 'order_address' && (
                <Box mt={1} p={1} style={{ backgroundColor: "#e3f2fd", borderRadius: 4, border: "1px solid #2196f3" }}>
                    <Typography variant="caption" style={{ color: "#1565c0", fontWeight: "bold" }}>
                        💡 Este bloco aguardará a resposta do usuário e salvará o texto como endereço do pedido.
                    </Typography>
                </Box>
            )}

            {selectedNode.type === 'order_summary' && (
                <>
                    <Box mt={1} p={1} style={{ backgroundColor: "#f3e5f5", borderRadius: 4, border: "1px solid #9c27b0" }}>
                        <Typography variant="caption" style={{ color: "#7b1fa2", fontWeight: "bold" }}>
                            💡 Este bloco enviará um resumo automático do pedido com botões Sim/Não.
                        </Typography>
                    </Box>
                    <div style={{ marginTop: "10px" }}>
                        <div className={classes.title}>Botões (Sim/Não)</div>
                        {buttons.map((btn, index) => (
                            <div key={index} style={{ display: "flex", gap: "5px", marginBottom: "5px" }}>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    value={btn.label}
                                    onChange={(e) => handleButtonChange(index, e.target.value)}
                                    fullWidth
                                />
                            </div>
                        ))}
                        {buttons.length === 0 && (
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                onClick={() => {
                                    const defaultButtons = [
                                        { id: "yes", label: "Sim" },
                                        { id: "no", label: "Não" }
                                    ];
                                    setButtons(defaultButtons);
                                    updateNodeData({ buttons: defaultButtons });
                                }}
                                style={{ marginTop: "5px" }}
                            >
                                Adicionar Botões Sim/Não
                            </Button>
                        )}
                    </div>
                </>
            )}

            {selectedNode.type === 'jump' && (
                <>
                    <Autocomplete
                        options={allNodes.filter(n => n.id !== selectedNode.id)}
                        getOptionLabel={(option) => {
                            const label = option.data?.label || option.data?.title || option.type;
                            return `${label} (${option.id})`;
                        }}
                        value={allNodes.find(n => n.id === targetNodeId) || null}
                        onChange={(event, newValue) => {
                            const newId = newValue ? newValue.id : "";
                            const newName = newValue ? (newValue.data?.label || newValue.data?.title || newValue.type) : "";
                            setTargetNodeId(newId);
                            setTargetNodeName(newName);
                            updateNodeData({
                                targetNodeId: newId,
                                targetNodeName: newName
                            });
                        }}
                        renderInput={(params) => (
                            <TextField {...params} label="Pular para o Card..." variant="outlined" fullWidth />
                        )}
                    />
                    <Box mt={1} p={1} style={{ backgroundColor: "#eceff1", borderRadius: 4, border: "1px solid #607d8b" }}>
                        <Typography variant="caption" style={{ color: "#455a64", fontWeight: "bold" }}>
                            💡 O fluxo saltará imediatamente para o card selecionado.
                        </Typography>
                    </Box>
                </>
            )}

            {selectedNode.type === 'confirmation' && (
                <>
                    <div style={{ marginTop: "10px" }}>
                        <div className={classes.title}>Buttons (Yes/No)</div>
                        {buttons.map((btn, index) => (
                            <div key={index} style={{ display: "flex", gap: "5px", marginBottom: "5px" }}>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    value={btn.label}
                                    onChange={(e) => handleButtonChange(index, e.target.value)}
                                    fullWidth
                                />
                            </div>
                        ))}
                        {buttons.length === 0 && (
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                onClick={() => {
                                    const defaultButtons = [
                                        { id: "yes", label: "Sim" },
                                        { id: "no", label: "Não" }
                                    ];
                                    setButtons(defaultButtons);
                                    updateNodeData({ buttons: defaultButtons });
                                }}
                                style={{ marginTop: "5px" }}
                            >
                                Add Yes/No Buttons
                            </Button>
                        )}
                    </div>
                </>
            )}

            {selectedNode.type === 'button' && (
                <>
                    <TextField
                        label="Title / Question"
                        multiline
                        rows={2}
                        variant="outlined"
                        value={label}
                        onChange={handleChange}
                        fullWidth
                    />
                    <div style={{ marginTop: "10px" }}>
                        <div className={classes.title}>Options</div>
                        {buttons.map((btn, index) => (
                            <div key={index} style={{ display: "flex", gap: "5px", marginBottom: "5px" }}>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    value={btn.label}
                                    onChange={(e) => handleButtonChange(index, e.target.value)}
                                    fullWidth
                                />
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    style={{ minWidth: "40px", padding: "0" }}
                                    onClick={() => handleRemoveButton(index)}
                                >
                                    X
                                </Button>
                            </div>
                        ))}
                        <Button variant="contained" color="primary" fullWidth onClick={handleAddButton} style={{ marginTop: "5px" }}>
                            Add Option
                        </Button>
                    </div>
                </>
            )}

            {selectedNode.type === 'list' && (
                <>
                    <TextField
                        label="Título da Lista"
                        multiline
                        rows={2}
                        variant="outlined"
                        value={label}
                        onChange={handleChange}
                        fullWidth
                    />
                    <div style={{ marginTop: "10px" }}>
                        <div className={classes.title}>Itens da Lista</div>
                        {listItems.map((item, index) => (
                            <div key={index} style={{ marginBottom: "10px", padding: "10px", background: "#f5f5f5", borderRadius: "5px" }}>
                                <TextField
                                    label="Título do Item"
                                    variant="outlined"
                                    size="small"
                                    value={item.title}
                                    onChange={(e) => handleListItemChange(index, 'title', e.target.value)}
                                    fullWidth
                                    style={{ marginBottom: "5px" }}
                                />
                                <TextField
                                    label="Descrição (opcional)"
                                    variant="outlined"
                                    size="small"
                                    value={item.description}
                                    onChange={(e) => handleListItemChange(index, 'description', e.target.value)}
                                    fullWidth
                                    style={{ marginBottom: "5px" }}
                                />
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    size="small"
                                    fullWidth
                                    onClick={() => handleRemoveListItem(index)}
                                >
                                    Remover Item
                                </Button>
                            </div>
                        ))}
                        <Button variant="contained" color="primary" fullWidth onClick={handleAddListItem} style={{ marginTop: "5px" }}>
                            Adicionar Item
                        </Button>
                    </div>
                </>
            )}

            {(selectedNode.type === 'image' || selectedNode.type === 'video') && (
                <>
                    <input
                        accept={selectedNode.type === 'image' ? "image/*" : "video/*"}
                        style={{ display: 'none' }}
                        id="media-upload"
                        type="file"
                        onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                                const formData = new FormData();
                                formData.append('file', file);

                                try {
                                    const { data } = await api.post('/chatbots/upload-media', formData, {
                                        headers: {
                                            'Content-Type': 'multipart/form-data'
                                        }
                                    });

                                    setUrl(data.url);
                                    updateNodeData({ url: data.url });
                                } catch (error) {
                                    console.error('Upload error:', error);
                                    alert('Erro ao fazer upload do arquivo');
                                }
                            }
                        }}
                    />
                    <label htmlFor="media-upload">
                        <Button variant="contained" color="primary" component="span" fullWidth>
                            Upload {selectedNode.type === 'image' ? 'Image' : 'Video'}
                        </Button>
                    </label>
                    {url && (
                        <div style={{ marginTop: "10px", textAlign: "center" }}>
                            {selectedNode.type === 'image' ? (
                                <img src={url} alt="Preview" style={{ maxWidth: "100%", maxHeight: "150px" }} />
                            ) : (
                                <video src={url} style={{ maxWidth: "100%", maxHeight: "150px" }} controls />
                            )}
                            <TextField
                                label="URL"
                                variant="outlined"
                                value={url}
                                size="small"
                                fullWidth
                                disabled
                                style={{ marginTop: "10px" }}
                            />
                        </div>
                    )}
                </>
            )}

            {selectedNode.type === 'pix_payment' && (
                <>
                    <TextField
                        label="Título *"
                        variant="outlined"
                        value={pixTitle}
                        onChange={(e) => {
                            setPixTitle(e.target.value);
                            updateNodeData({ title: e.target.value });
                        }}
                        fullWidth
                        placeholder="Ex: Detalhes do pedido"
                        style={{ marginBottom: "10px" }}
                    />

                    <TextField
                        label="Valor (R$) *"
                        type="number"
                        variant="outlined"
                        value={pixAmount}
                        onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            setPixAmount(val);
                            updateNodeData({ amount: val });
                        }}
                        fullWidth
                        inputProps={{ step: "0.01", min: "0" }}
                        style={{ marginBottom: "10px" }}
                    />

                    <TextField
                        label="Chave PIX *"
                        variant="outlined"
                        value={pixKey}
                        onChange={(e) => {
                            setPixKey(e.target.value);
                            updateNodeData({ pixKey: e.target.value });
                        }}
                        fullWidth
                        placeholder="Ex: 123e4567-e89b-12d3-a456-426614174000"
                        style={{ marginBottom: "10px" }}
                    />

                    <TextField
                        select
                        label="Tipo de Chave PIX"
                        variant="outlined"
                        value={pixType}
                        onChange={(e) => {
                            setPixType(e.target.value);
                            updateNodeData({ pixType: e.target.value });
                        }}
                        fullWidth
                        SelectProps={{ native: true }}
                        style={{ marginBottom: "10px" }}
                    >
                        <option value="CPF">CPF</option>
                        <option value="CNPJ">CNPJ</option>
                        <option value="PHONE">Telefone</option>
                        <option value="EMAIL">Email</option>
                        <option value="EVP">Chave Privada</option>
                    </TextField>

                    <TextField
                        label="Nome do Recebedor"
                        variant="outlined"
                        value={pixName}
                        onChange={(e) => {
                            setPixName(e.target.value);
                            updateNodeData({ pixName: e.target.value });
                        }}
                        fullWidth
                        placeholder="Ex: Loja Exemplo"
                        style={{ marginBottom: "10px" }}
                    />

                    <TextField
                        label="Número da Fatura"
                        variant="outlined"
                        value={pixInvoiceNumber}
                        onChange={(e) => {
                            setPixInvoiceNumber(e.target.value);
                            updateNodeData({ invoiceNumber: e.target.value });
                        }}
                        fullWidth
                        placeholder="Ex: PED-123"
                        style={{ marginBottom: "10px" }}
                    />

                    <TextField
                        label="Mensagem"
                        variant="outlined"
                        multiline
                        rows={2}
                        value={pixText}
                        onChange={(e) => {
                            setPixText(e.target.value);
                            updateNodeData({ text: e.target.value });
                        }}
                        fullWidth
                        placeholder="Ex: Pedido #123 pronto para pagamento"
                        style={{ marginBottom: "10px" }}
                    />

                    <TextField
                        label="Rodapé"
                        variant="outlined"
                        value={pixFooter}
                        onChange={(e) => {
                            setPixFooter(e.target.value);
                            updateNodeData({ footer: e.target.value });
                        }}
                        fullWidth
                        placeholder="Ex: Loja Exemplo"
                        style={{ marginBottom: "10px" }}
                    />

                    <TextField
                        label="Nome do Item"
                        variant="outlined"
                        value={pixItemName}
                        onChange={(e) => {
                            setPixItemName(e.target.value);
                            updateNodeData({ itemName: e.target.value });
                        }}
                        fullWidth
                        placeholder="Ex: Assinatura Plano Ouro"
                        style={{ marginBottom: "10px" }}
                    />

                    <div style={{ fontSize: "10px", color: "#999", marginTop: "10px" }}>
                        * Campos obrigatórios
                    </div>
                </>
            )}

            {selectedNode.type === 'trigger_condition' && (
                <>
                    <TextField
                        label="Título do Bloco"
                        variant="outlined"
                        value={label}
                        onChange={handleChange}
                        fullWidth
                        placeholder="Ex: Verificar intenção de cadastro"
                        style={{ marginBottom: "15px" }}
                    />

                    <div className={classes.title}>Gatilhos</div>
                    <div style={{ fontSize: "11px", color: "#666", marginBottom: "10px" }}>
                        Digite um gatilho por linha. A comparação é case-insensitive (ignora maiúsculas/minúsculas).
                    </div>

                    <TextField
                        multiline
                        rows={6}
                        variant="outlined"
                        value={triggers.join('\n')}
                        onChange={(e) => {
                            const newTriggers = e.target.value
                                .split('\n')
                                .map(t => t.trim());
                            setTriggers(newTriggers);
                            // Filter empty lines only when saving to node data
                            const filteredTriggers = newTriggers.filter(t => t.length > 0);
                            updateNodeData({ triggers: filteredTriggers });
                        }}
                        fullWidth
                        placeholder="cadastrar&#10;quero me cadastrar&#10;registrar&#10;fazer cadastro"
                        helperText={`Total: ${triggers.filter(t => t.length > 0).length} gatilho(s)`}
                    />

                    <div style={{
                        fontSize: "10px",
                        color: "#ff9800",
                        marginTop: "10px",
                        padding: "8px",
                        background: "#fff3e0",
                        borderRadius: "4px"
                    }}>
                        <strong>⚠️ Importante:</strong><br />
                        • Match exato: "cadastrar" ≠ "cadastro"<br />
                        • Ignora maiúsculas: "CADASTRAR" = "cadastrar"<br />
                        • Conecte a saída <span style={{ color: "#4CAF50" }}>✓ SIM</span> para quando houver match<br />
                        • Conecte a saída <span style={{ color: "#f44336" }}>✗ NÃO</span> para quando não houver match
                    </div>
                </>
            )}

            {selectedNode.type === 'tag_block' && (
                <>
                    <TextField
                        label="Título do Bloco"
                        variant="outlined"
                        value={label}
                        onChange={handleChange}
                        fullWidth
                        placeholder="Ex: Adicionar Tag Financeiro"
                        style={{ marginBottom: "15px" }}
                    />

                    <div className={classes.title}>Selecionar Tags</div>
                    <Autocomplete
                        multiple
                        id="tags-outlined"
                        options={tagOptions}
                        getOptionLabel={(option) => option.name}
                        getOptionSelected={(option, value) => option.id === value.id}
                        value={selectedTags}
                        onChange={(e, newValue) => {
                            setSelectedTags(newValue);
                            updateNodeData({ tags: newValue });
                        }}
                        onOpen={async () => {
                            setTagsLoading(true);
                            try {
                                const { data } = await api.get("/tags/list");
                                setTagOptions(data);
                            } catch (err) {
                                console.error(err);
                            }
                            setTagsLoading(false);
                        }}
                        loading={tagsLoading}
                        renderTags={(tagValue, getTagProps) =>
                            tagValue.map((option, index) => (
                                <Chip
                                    label={option.name}
                                    {...getTagProps({ index })}
                                    style={{ backgroundColor: option.color || "#eee", color: "#fff" }}
                                    size="small"
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                placeholder="Selecione as tags..."
                            />
                        )}
                    />
                    <div style={{ fontSize: "10px", color: "#999", marginTop: "10px" }}>
                        As tags selecionadas serão adicionadas ao contato automaticamente.
                    </div>
                </>
            )}

            {selectedNode.type === 'chatgpt' && (
                <>
                    <div className={classes.title} style={{ color: "#10a37f" }}>🤖 Configuração ChatGPT</div>

                    <TextField
                        label="OpenAI API Key *"
                        type="password"
                        variant="outlined"
                        value={chatgptApiKey}
                        onChange={(e) => {
                            setChatgptApiKey(e.target.value);
                            updateNodeData({ apiKey: e.target.value });
                        }}
                        fullWidth
                        placeholder="sk-..."
                        style={{ marginBottom: "10px" }}
                        helperText="Obtenha em: platform.openai.com"
                    />

                    <TextField
                        select
                        label="Modelo"
                        variant="outlined"
                        value={chatgptModel}
                        onChange={(e) => {
                            setChatgptModel(e.target.value);
                            updateNodeData({ model: e.target.value });
                        }}
                        fullWidth
                        SelectProps={{ native: true }}
                        style={{ marginBottom: "10px" }}
                    >
                        <option value="gpt-3.5-turbo">GPT-3.5 Turbo (Rápido e Econômico)</option>
                        <option value="gpt-4">GPT-4 (Mais Inteligente)</option>
                        <option value="gpt-4-turbo">GPT-4 Turbo</option>
                    </TextField>

                    <TextField
                        label="Prompt do Sistema (Treinamento) *"
                        multiline
                        rows={6}
                        variant="outlined"
                        value={chatgptSystemPrompt}
                        onChange={(e) => {
                            setChatgptSystemPrompt(e.target.value);
                            updateNodeData({ systemPrompt: e.target.value });
                        }}
                        fullWidth
                        placeholder="Você é um assistente virtual de uma clínica médica. Seja educado e prestativo..."
                        style={{ marginBottom: "10px" }}
                        helperText="Defina como a IA deve se comportar"
                    />

                    <TextField
                        label="Temperatura (Criatividade)"
                        type="number"
                        variant="outlined"
                        value={chatgptTemperature}
                        onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            setChatgptTemperature(val);
                            updateNodeData({ temperature: val });
                        }}
                        fullWidth
                        inputProps={{ step: "0.1", min: "0", max: "1" }}
                        style={{ marginBottom: "10px" }}
                        helperText="0 = Preciso, 1 = Criativo"
                    />

                    <TextField
                        label="Máximo de Tokens"
                        type="number"
                        variant="outlined"
                        value={chatgptMaxTokens}
                        onChange={(e) => {
                            const val = parseInt(e.target.value) || 0;
                            setChatgptMaxTokens(val);
                            updateNodeData({ maxTokens: val });
                        }}
                        fullWidth
                        inputProps={{ step: "50", min: "50", max: "2000" }}
                        style={{ marginBottom: "10px" }}
                        helperText="Limite de tokens na resposta"
                    />

                    <TextField
                        label="Histórico de Conversação"
                        type="number"
                        variant="outlined"
                        value={chatgptHistoryLimit}
                        onChange={(e) => {
                            const val = parseInt(e.target.value) || 0;
                            setChatgptHistoryLimit(val);
                            updateNodeData({ conversationHistory: val });
                        }}
                        fullWidth
                        inputProps={{ step: "1", min: "1", max: "20" }}
                        style={{ marginBottom: "10px" }}
                        helperText="Quantas mensagens manter em contexto"
                    />

                    {/* Intent Detection Section */}
                    <div style={{
                        borderTop: "1px solid #e0e0e0",
                        paddingTop: "15px",
                        marginTop: "15px"
                    }}>
                        <div className={classes.title} style={{ color: "#10a37f", marginBottom: "10px" }}>
                            🎯 Detecção de Intenções
                        </div>

                        <div style={{
                            fontSize: "11px",
                            color: "#666",
                            marginBottom: "10px",
                            padding: "8px",
                            background: "#f5f5f5",
                            borderRadius: "4px"
                        }}>
                            Configure keywords para redirecionar automaticamente para outros fluxos (agendamento, PIX, etc)
                        </div>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={intentDetectionEnabled}
                                    onChange={(e) => {
                                        console.log("[PropertiesPanel] Checkbox clicked:", e.target.checked);
                                        const newEnabled = e.target.checked;

                                        // Update local state immediately for UI
                                        setIntentDetectionEnabled(newEnabled);

                                        // Update node data
                                        const currentIntents = intentsList;
                                        console.log("[PropertiesPanel] Current intents:", currentIntents);
                                        updateNodeData({
                                            intentDetection: {
                                                enabled: newEnabled,
                                                intents: currentIntents
                                            }
                                        });
                                        console.log("[PropertiesPanel] updateNodeData called");
                                    }}
                                    color="primary"
                                />
                            }
                            label="Habilitar Detecção de Intenções"
                            style={{ marginBottom: "15px" }}
                        />

                        {intentDetectionEnabled && (
                            <>
                                {intentsList.map((intent, index) => (
                                    <div key={index} style={{
                                        padding: "12px",
                                        background: "#f9f9f9",
                                        borderRadius: "6px",
                                        marginBottom: "10px",
                                        border: "1px solid #e0e0e0"
                                    }}>
                                        <div style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            marginBottom: "8px"
                                        }}>
                                            <strong style={{ fontSize: "12px", color: "#10a37f" }}>
                                                Intenção #{index + 1}
                                            </strong>
                                            <Button
                                                size="small"
                                                color="secondary"
                                                onClick={() => {
                                                    const newIntents = [...intentsList];
                                                    newIntents.splice(index, 1);
                                                    setIntentsList(newIntents);
                                                    updateNodeData({
                                                        intentDetection: {
                                                            enabled: true,
                                                            intents: newIntents
                                                        }
                                                    });
                                                }}
                                                style={{ minWidth: "30px", padding: "2px 8px" }}
                                            >
                                                ✕
                                            </Button>
                                        </div>

                                        <TextField
                                            label="Nome da Intenção"
                                            variant="outlined"
                                            size="small"
                                            value={intent.name || ""}
                                            onChange={(e) => {
                                                const newIntents = [...intentsList];
                                                newIntents[index] = { ...newIntents[index], name: e.target.value };
                                                setIntentsList(newIntents);
                                                updateNodeData({
                                                    intentDetection: {
                                                        enabled: true,
                                                        intents: newIntents
                                                    }
                                                });
                                            }}
                                            fullWidth
                                            placeholder="Ex: agendamento"
                                            style={{ marginBottom: "8px" }}
                                        />

                                        <TextField
                                            label="Keywords (separadas por vírgula)"
                                            variant="outlined"
                                            size="small"
                                            value={(intent.keywords || []).join(", ")}
                                            onChange={(e) => {
                                                const newIntents = [...intentsList];
                                                // Store the raw string value temporarily
                                                const rawValue = e.target.value;
                                                // Only split and filter when there's actual content
                                                const keywords = rawValue.split(",").map(k => k.trim());
                                                newIntents[index] = { ...newIntents[index], keywords };
                                                setIntentsList(newIntents);
                                            }}
                                            onBlur={(e) => {
                                                // Clean up empty keywords on blur
                                                const newIntents = [...intentsList];
                                                const keywords = e.target.value.split(",").map(k => k.trim()).filter(k => k);
                                                newIntents[index] = { ...newIntents[index], keywords };
                                                setIntentsList(newIntents);
                                                updateNodeData({
                                                    intentDetection: {
                                                        enabled: true,
                                                        intents: newIntents
                                                    }
                                                });
                                            }}
                                            fullWidth
                                            placeholder="agendar, marcar, consulta"
                                            helperText={`${(intent.keywords || []).filter(k => k).length} keyword(s)`}
                                            style={{ marginBottom: "8px" }}
                                        />

                                        <div style={{ fontSize: "10px", color: "#666", fontStyle: "italic" }}>
                                            Conecte a saída lateral "{intent.name || 'intenção'}" ao node desejado
                                        </div>
                                    </div>
                                ))}

                                <Button
                                    variant="outlined"
                                    color="primary"
                                    fullWidth
                                    onClick={() => {
                                        const newIntent = {
                                            name: "",
                                            keywords: [],
                                            targetNodeId: ""
                                        };
                                        const newIntents = [...intentsList, newIntent];
                                        setIntentsList(newIntents);
                                        updateNodeData({
                                            intentDetection: {
                                                enabled: true,
                                                intents: newIntents
                                            }
                                        });
                                    }}
                                    style={{ marginTop: "5px" }}
                                >
                                    + Adicionar Intenção
                                </Button>
                            </>
                        )}
                    </div>

                    <div style={{
                        fontSize: "11px",
                        color: "#10a37f",
                        marginTop: "15px",
                        padding: "10px",
                        background: "#f0fdf4",
                        borderRadius: "4px",
                        border: "1px solid #10a37f"
                    }}>
                        <strong>💡 Como funciona:</strong><br />
                        1. Usuário envia mensagem<br />
                        2. ChatGPT processa com seu prompt<br />
                        3. Se detectar keyword, redireciona<br />
                        4. Senão, responde e aguarda<br />
                        <br />
                        <strong>Exemplo:</strong><br />
                        Usuário: "Quero agendar"<br />
                        → Detecta "agendar"<br />
                        → Redireciona para Agendamento
                    </div>
                </>
            )}

            {selectedNode.type === 'return_to_chatgpt' && (
                <>
                    <div className={classes.title} style={{ color: "#10a37f" }}>🔙 Retornar ao ChatGPT</div>

                    <TextField
                        label="Mensagem de Retorno"
                        multiline
                        rows={3}
                        variant="outlined"
                        value={returnMessage}
                        onChange={(e) => {
                            setReturnMessage(e.target.value);
                            updateNodeData({ message: e.target.value });
                        }}
                        fullWidth
                        placeholder="Pronto! Posso ajudar com mais alguma coisa?"
                        style={{ marginBottom: "10px" }}
                        helperText="Mensagem enviada ao retornar ao ChatGPT"
                    />

                    <div style={{
                        fontSize: "11px",
                        color: "#10a37f",
                        marginTop: "10px",
                        padding: "10px",
                        background: "#f0fdf4",
                        borderRadius: "4px",
                        border: "1px solid #10a37f"
                    }}>
                        <strong>💡 Como funciona:</strong><br />
                        1. Coloque após um sub-fluxo (ex: agendamento)<br />
                        2. Envia mensagem configurada<br />
                        3. Retorna ao node ChatGPT anterior<br />
                        4. Continua conversação com IA<br />
                        <br />
                        <strong>Exemplo de uso:</strong><br />
                        ChatGPT → Agendamento → Confirmação → <strong>Retornar ao ChatGPT</strong> → ChatGPT
                    </div>
                </>
            )}

            {selectedNode.type === 'perplexity' && (
                <>
                    <div className={classes.title} style={{ color: "#20808d" }}>🔍 Configuração Perplexity</div>

                    <TextField
                        label="Perplexity API Key *"
                        type="password"
                        variant="outlined"
                        value={perplexityApiKey}
                        onChange={(e) => {
                            setPerplexityApiKey(e.target.value);
                            updateNodeData({ apiKey: e.target.value });
                        }}
                        fullWidth
                        placeholder="pplx-..."
                        style={{ marginBottom: "10px" }}
                        helperText="Obtenha em: perplexity.ai/settings/api"
                    />

                    <TextField
                        select
                        label="Modelo"
                        variant="outlined"
                        value={perplexityModel}
                        onChange={(e) => {
                            setPerplexityModel(e.target.value);
                            updateNodeData({ model: e.target.value });
                        }}
                        fullWidth
                        SelectProps={{ native: true }}
                        style={{ marginBottom: "10px" }}
                        helperText="Modelos 'online' têm busca web integrada"
                    >
                        <option value="sonar">Sonar (Rápido + Web)</option>
                        <option value="sonar-pro">Sonar Pro (Avançado + Web)</option>
                        <option value="sonar-reasoning-pro">Sonar Reasoning Pro (Raciocínio)</option>
                        <option value="sonar-deep-research">Sonar Deep Research (Pesquisa Profunda)</option>
                    </TextField>

                    <TextField
                        label="System Prompt"
                        variant="outlined"
                        multiline
                        rows={6}
                        value={perplexitySystemPrompt}
                        onChange={(e) => {
                            setPerplexitySystemPrompt(e.target.value);
                            updateNodeData({ systemPrompt: e.target.value });
                        }}
                        fullWidth
                        placeholder="Você é um assistente virtual prestativo com acesso a informações atualizadas da web..."
                        style={{ marginBottom: "10px" }}
                        helperText="Instruções para treinar a IA"
                    />

                    <TextField
                        label="Temperatura"
                        type="number"
                        variant="outlined"
                        value={perplexityTemperature}
                        onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0.7;
                            setPerplexityTemperature(val);
                            updateNodeData({ temperature: val });
                        }}
                        fullWidth
                        inputProps={{ step: "0.1", min: "0", max: "2" }}
                        style={{ marginBottom: "10px" }}
                        helperText="0 = Preciso, 2 = Criativo"
                    />

                    <TextField
                        label="Max Tokens"
                        type="number"
                        variant="outlined"
                        value={perplexityMaxTokens}
                        onChange={(e) => {
                            const val = parseInt(e.target.value) || 500;
                            setPerplexityMaxTokens(val);
                            updateNodeData({ maxTokens: val });
                        }}
                        fullWidth
                        inputProps={{ step: "50", min: "50", max: "4000" }}
                        style={{ marginBottom: "10px" }}
                        helperText="Limite de tamanho da resposta"
                    />

                    <TextField
                        label="Histórico de Conversação"
                        type="number"
                        variant="outlined"
                        value={perplexityHistoryLimit}
                        onChange={(e) => {
                            const val = parseInt(e.target.value) || 5;
                            setPerplexityHistoryLimit(val);
                            updateNodeData({ conversationHistory: val });
                        }}
                        fullWidth
                        inputProps={{ step: "1", min: "1", max: "20" }}
                        style={{ marginBottom: "10px" }}
                        helperText="Quantas mensagens manter em contexto"
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={perplexityReturnQuestions}
                                onChange={(e) => {
                                    setPerplexityReturnQuestions(e.target.checked);
                                    updateNodeData({ returnRelatedQuestions: e.target.checked });
                                }}
                                color="primary"
                            />
                        }
                        label="Retornar Perguntas Relacionadas"
                        style={{ marginBottom: "15px" }}
                    />

                    {/* Intent Detection Section */}
                    <div style={{
                        borderTop: "1px solid #e0e0e0",
                        paddingTop: "15px",
                        marginTop: "15px"
                    }}>
                        <div className={classes.title} style={{ color: "#20808d", marginBottom: "10px" }}>
                            🎯 Detecção de Intenções
                        </div>

                        <div style={{
                            fontSize: "11px",
                            color: "#666",
                            marginBottom: "10px",
                            padding: "8px",
                            background: "#f5f5f5",
                            borderRadius: "4px"
                        }}>
                            Configure keywords para redirecionar automaticamente para outros fluxos
                        </div>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={perplexityIntentEnabled}
                                    onChange={(e) => {
                                        const newEnabled = e.target.checked;
                                        setPerplexityIntentEnabled(newEnabled);
                                        updateNodeData({
                                            intentDetection: {
                                                enabled: newEnabled,
                                                intents: perplexityIntentsList
                                            }
                                        });
                                    }}
                                    color="primary"
                                />
                            }
                            label="Habilitar Detecção de Intenções"
                            style={{ marginBottom: "15px" }}
                        />

                        {perplexityIntentEnabled && (
                            <>
                                {perplexityIntentsList.map((intent, index) => (
                                    <div key={index} style={{
                                        padding: "12px",
                                        background: "#f9f9f9",
                                        borderRadius: "6px",
                                        marginBottom: "10px",
                                        border: "1px solid #e0e0e0"
                                    }}>
                                        <div style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            marginBottom: "8px"
                                        }}>
                                            <strong style={{ fontSize: "12px", color: "#20808d" }}>
                                                Intenção #{index + 1}
                                            </strong>
                                            <Button
                                                size="small"
                                                color="secondary"
                                                onClick={() => {
                                                    const newIntents = [...perplexityIntentsList];
                                                    newIntents.splice(index, 1);
                                                    setPerplexityIntentsList(newIntents);
                                                    updateNodeData({
                                                        intentDetection: {
                                                            enabled: true,
                                                            intents: newIntents
                                                        }
                                                    });
                                                }}
                                                style={{ minWidth: "30px", padding: "2px 8px" }}
                                            >
                                                ✕
                                            </Button>
                                        </div>

                                        <TextField
                                            label="Nome da Intenção"
                                            variant="outlined"
                                            size="small"
                                            value={intent.name || ""}
                                            onChange={(e) => {
                                                const newIntents = [...perplexityIntentsList];
                                                newIntents[index] = { ...newIntents[index], name: e.target.value };
                                                setPerplexityIntentsList(newIntents);
                                                updateNodeData({
                                                    intentDetection: {
                                                        enabled: true,
                                                        intents: newIntents
                                                    }
                                                });
                                            }}
                                            fullWidth
                                            placeholder="Ex: agendamento"
                                            style={{ marginBottom: "8px" }}
                                        />

                                        <TextField
                                            label="Keywords (separadas por vírgula)"
                                            variant="outlined"
                                            size="small"
                                            value={(intent.keywords || []).join(", ")}
                                            onChange={(e) => {
                                                const newIntents = [...perplexityIntentsList];
                                                const rawValue = e.target.value;
                                                const keywords = rawValue.split(",").map(k => k.trim());
                                                newIntents[index] = { ...newIntents[index], keywords };
                                                setPerplexityIntentsList(newIntents);
                                            }}
                                            onBlur={(e) => {
                                                const newIntents = [...perplexityIntentsList];
                                                const keywords = e.target.value.split(",").map(k => k.trim()).filter(k => k);
                                                newIntents[index] = { ...newIntents[index], keywords };
                                                setPerplexityIntentsList(newIntents);
                                                updateNodeData({
                                                    intentDetection: {
                                                        enabled: true,
                                                        intents: newIntents
                                                    }
                                                });
                                            }}
                                            fullWidth
                                            placeholder="agendar, marcar, consulta"
                                            helperText={`${(intent.keywords || []).filter(k => k).length} keyword(s)`}
                                            style={{ marginBottom: "8px" }}
                                        />

                                        <div style={{ fontSize: "10px", color: "#666", fontStyle: "italic" }}>
                                            Conecte a saída lateral "{intent.name || 'intenção'}" ao node desejado
                                        </div>
                                    </div>
                                ))}

                                <Button
                                    variant="outlined"
                                    color="primary"
                                    fullWidth
                                    onClick={() => {
                                        const newIntent = {
                                            name: "",
                                            keywords: [],
                                            targetNodeId: ""
                                        };
                                        const newIntents = [...perplexityIntentsList, newIntent];
                                        setPerplexityIntentsList(newIntents);
                                        updateNodeData({
                                            intentDetection: {
                                                enabled: true,
                                                intents: newIntents
                                            }
                                        });
                                    }}
                                    style={{ marginTop: "5px" }}
                                >
                                    + Adicionar Intenção
                                </Button>
                            </>
                        )}
                    </div>

                    <div style={{
                        fontSize: "11px",
                        color: "#20808d",
                        marginTop: "15px",
                        padding: "10px",
                        background: "#e6f7f9",
                        borderRadius: "4px",
                        border: "1px solid #20808d"
                    }}>
                        <strong>💡 Como funciona:</strong><br />
                        1. Usuário envia mensagem<br />
                        2. Perplexity busca na web (modelos online)<br />
                        3. Se detectar keyword, redireciona<br />
                        4. Senão, responde e aguarda<br />
                        <br />
                        <strong>🌐 Diferencial:</strong><br />
                        Modelos "online" têm acesso a informações<br />
                        atualizadas da web em tempo real!
                    </div>
                </>
            )}

            {selectedNode.type === 'return_to_perplexity' && (
                <>
                    <div className={classes.title} style={{ color: "#20808d" }}>🔙 Retornar ao Perplexity</div>

                    <TextField
                        label="Mensagem de Retorno"
                        multiline
                        rows={3}
                        variant="outlined"
                        value={returnPerplexityMessage}
                        onChange={(e) => {
                            setReturnPerplexityMessage(e.target.value);
                            updateNodeData({ message: e.target.value });
                        }}
                        fullWidth
                        placeholder="Pronto! Posso ajudar com mais alguma coisa?"
                        style={{ marginBottom: "10px" }}
                        helperText="Mensagem enviada ao retornar ao Perplexity"
                    />

                    <div style={{
                        fontSize: "11px",
                        color: "#20808d",
                        marginTop: "10px",
                        padding: "10px",
                        background: "#e6f7f9",
                        borderRadius: "4px",
                        border: "1px solid #20808d"
                    }}>
                        <strong>💡 Como funciona:</strong><br />
                        1. Coloque após um sub-fluxo (ex: agendamento)<br />
                        2. Envia mensagem configurada<br />
                        3. Retorna ao node Perplexity anterior<br />
                        4. Continua conversação com IA<br />
                        <br />
                        <strong>Exemplo de uso:</strong><br />
                        Perplexity → Agendamento → Confirmação → <strong>Retornar ao Perplexity</strong> → Perplexity
                    </div>
                </>
            )}

            {selectedNode.type === 'carousel' && (
                <>
                    <TextField
                        label="Texto do Carrossel"
                        multiline
                        rows={3}
                        variant="outlined"
                        value={label}
                        onChange={handleChange}
                        fullWidth
                        placeholder="Digite o texto principal do carrossel..."
                        style={{ marginBottom: "15px" }}
                    />

                    <div className={classes.title}>Cards do Carrossel</div>
                    <div style={{ fontSize: "11px", color: "#666", marginBottom: "10px" }}>
                        Adicione cards com imagem, título e botões. Cada botão pode ser conectado a outros nodes.
                    </div>

                    {(selectedNode.data.cards || []).map((card, cardIndex) => (
                        <div key={cardIndex} style={{
                            marginBottom: "15px",
                            padding: "12px",
                            background: "#fff3e0",
                            borderRadius: "8px",
                            border: "2px solid #ff6f00"
                        }}>
                            <div style={{ fontWeight: "bold", marginBottom: "8px", color: "#ff6f00" }}>
                                Card {cardIndex + 1}
                            </div>

                            <TextField
                                label="Título do Card"
                                variant="outlined"
                                size="small"
                                value={card.title || ""}
                                onChange={(e) => {
                                    const newCards = [...(selectedNode.data.cards || [])];
                                    newCards[cardIndex].title = e.target.value;
                                    updateNodeData({ cards: newCards });
                                }}
                                fullWidth
                                style={{ marginBottom: "8px" }}
                            />

                            <input
                                accept="image/*"
                                style={{ display: 'none' }}
                                id={`carousel-image-upload-${cardIndex}`}
                                type="file"
                                onChange={async (e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                        const formData = new FormData();
                                        formData.append('file', file);

                                        try {
                                            const { data } = await api.post('/chatbots/upload-media', formData, {
                                                headers: {
                                                    'Content-Type': 'multipart/form-data'
                                                }
                                            });

                                            const newCards = [...(selectedNode.data.cards || [])];
                                            newCards[cardIndex].image = data.url;
                                            updateNodeData({ cards: newCards });
                                        } catch (error) {
                                            console.error('Upload error:', error);
                                            alert('Erro ao fazer upload da imagem');
                                        }
                                    }
                                }}
                            />
                            <label htmlFor={`carousel-image-upload-${cardIndex}`}>
                                <Button
                                    variant="outlined"
                                    component="span"
                                    fullWidth
                                    size="small"
                                    style={{ marginBottom: "8px" }}
                                >
                                    {card.image ? "🖼️ Trocar Imagem" : "📷 Upload Imagem"}
                                </Button>
                            </label>

                            {card.image && (
                                <div style={{ marginBottom: "8px", textAlign: "center" }}>
                                    <img src={card.image} alt="Preview" style={{ maxWidth: "100%", maxHeight: "80px", borderRadius: "4px" }} />
                                </div>
                            )}

                            <div style={{ fontSize: "11px", fontWeight: "bold", marginTop: "10px", marginBottom: "5px" }}>
                                Botões:
                            </div>
                            {(card.buttons || []).map((btn, btnIndex) => (
                                <div key={btnIndex} style={{ display: "flex", gap: "5px", marginBottom: "5px" }}>
                                    <TextField
                                        variant="outlined"
                                        size="small"
                                        value={btn.label || ""}
                                        onChange={(e) => {
                                            const newCards = [...(selectedNode.data.cards || [])];
                                            newCards[cardIndex].buttons[btnIndex].label = e.target.value;
                                            updateNodeData({ cards: newCards });
                                        }}
                                        fullWidth
                                        placeholder={`Botão ${btnIndex + 1}`}
                                    />
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        size="small"
                                        style={{ minWidth: "40px" }}
                                        onClick={() => {
                                            const newCards = [...(selectedNode.data.cards || [])];
                                            newCards[cardIndex].buttons.splice(btnIndex, 1);
                                            updateNodeData({ cards: newCards });
                                        }}
                                    >
                                        X
                                    </Button>
                                </div>
                            ))}

                            {(card.buttons || []).length < 3 && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    onClick={() => {
                                        const newCards = [...(selectedNode.data.cards || [])];
                                        if (!newCards[cardIndex].buttons) newCards[cardIndex].buttons = [];
                                        newCards[cardIndex].buttons.push({
                                            id: `btn_${new Date().getTime()}`,
                                            label: ""
                                        });
                                        updateNodeData({ cards: newCards });
                                    }}
                                    style={{ marginTop: "5px" }}
                                >
                                    + Adicionar Botão
                                </Button>
                            )}

                            <Button
                                variant="outlined"
                                color="secondary"
                                size="small"
                                fullWidth
                                onClick={() => {
                                    const newCards = [...(selectedNode.data.cards || [])];
                                    newCards.splice(cardIndex, 1);
                                    updateNodeData({ cards: newCards });
                                }}
                                style={{ marginTop: "10px" }}
                            >
                                🗑️ Remover Card
                            </Button>
                        </div>
                    ))}

                    {(!selectedNode.data.cards || selectedNode.data.cards.length < 10) && (
                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            onClick={() => {
                                const newCards = [...(selectedNode.data.cards || []), {
                                    id: `card_${new Date().getTime()}`,
                                    title: "",
                                    image: "",
                                    buttons: [
                                        { id: `btn_${new Date().getTime()}`, label: "" }
                                    ]
                                }];
                                updateNodeData({ cards: newCards });
                            }}
                            style={{ marginTop: "10px" }}
                        >
                            + Adicionar Card
                        </Button>
                    )}

                    <div style={{
                        fontSize: "10px",
                        color: "#ff6f00",
                        marginTop: "15px",
                        padding: "10px",
                        background: "#fff3e0",
                        borderRadius: "4px",
                        border: "1px solid #ff6f00"
                    }}>
                        <strong>💡 Dica:</strong><br />
                        • Máximo de 10 cards<br />
                        • Cada card pode ter até 3 botões<br />
                        • Conecte os botões a outros nodes do fluxo<br />
                        • Imagens são obrigatórias para cada card
                    </div>
                </>
            )}

            {selectedNode.type === 'products' && (
                <>
                    <TextField
                        label="Mensagem (opcional)"
                        multiline
                        rows={2}
                        variant="outlined"
                        value={label}
                        onChange={handleChange}
                        fullWidth
                    />

                    <div style={{ marginTop: "15px" }}>
                        <div className={classes.title}>Selecionar Produtos</div>

                        {productsLoading ? (
                            <div style={{ textAlign: "center", padding: "20px" }}>
                                Carregando produtos...
                            </div>
                        ) : products.length === 0 ? (
                            <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                                Nenhum produto cadastrado
                            </div>
                        ) : (
                            <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                                {products.map((product) => {
                                    const isSelected = selectedProducts.includes(product.id);
                                    return (
                                        <div
                                            key={product.id}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "10px",
                                                padding: "10px",
                                                marginBottom: "8px",
                                                background: isSelected ? "#e8f5e9" : "#f5f5f5",
                                                borderRadius: "5px",
                                                border: isSelected ? "2px solid #4caf50" : "1px solid #ddd",
                                                cursor: "pointer"
                                            }}
                                            onClick={() => {
                                                const newSelected = isSelected
                                                    ? selectedProducts.filter(id => id !== product.id)
                                                    : [...selectedProducts, product.id];
                                                setSelectedProducts(newSelected);

                                                const selectedProductsData = products.filter(p => newSelected.includes(p.id));
                                                updateNodeData({
                                                    selectedProducts: newSelected,
                                                    products: selectedProductsData
                                                });
                                            }}
                                        >
                                            <Checkbox
                                                checked={isSelected}
                                                color="primary"
                                            />
                                            {product.image ? (
                                                <img
                                                    src={product.image.startsWith('http') ? product.image : `${process.env.REACT_APP_BACKEND_URL || window.location.origin}${product.image}`}
                                                    alt={product.name}
                                                    style={{
                                                        width: "50px",
                                                        height: "50px",
                                                        objectFit: "cover",
                                                        borderRadius: "4px"
                                                    }}
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                    }}
                                                />
                                            ) : (
                                                <div style={{
                                                    width: "50px",
                                                    height: "50px",
                                                    background: "#eee",
                                                    borderRadius: "4px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontSize: "20px",
                                                    color: "#999"
                                                }}>
                                                    📷
                                                </div>
                                            )}
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontWeight: "bold", fontSize: "13px" }}>
                                                    {product.name}
                                                </div>
                                                <div style={{ fontSize: "12px", color: "#4caf50", fontWeight: "bold" }}>
                                                    R$ {parseFloat(product.price).toFixed(2)}
                                                </div>
                                                {product.description && (
                                                    <div style={{
                                                        fontSize: "11px",
                                                        color: "#666",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap"
                                                    }}>
                                                        {product.description}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        <div style={{
                            fontSize: "11px",
                            color: "#4caf50",
                            marginTop: "10px",
                            fontWeight: "bold"
                        }}>
                            {selectedProducts.length} produto(s) selecionado(s)
                        </div>

                        <div style={{
                            fontSize: "10px",
                            color: "#ff6f00",
                            marginTop: "15px",
                            padding: "10px",
                            background: "#fff3e0",
                            borderRadius: "4px",
                            border: "1px solid #ff6f00"
                        }}>
                            <strong>💡 Dica:</strong><br />
                            • Selecione os produtos que deseja exibir<br />
                            • Serão enviados como carrossel no WhatsApp<br />
                            • Máximo de 10 produtos por vez<br />
                            • Cliente clicará no botão com o nome do produto
                        </div>
                    </div>
                </>
            )}

            <div style={{ fontSize: "10px", color: "#999", marginTop: "20px" }}>
                Node ID: {selectedNode.id}
            </div>
        </aside>
    );
};

